import HandIcon from 'assets/images/easyPay/init.svg'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import DotsLoading from 'components/loadings/dotsLoading'
import useSetInitTransaction from './hooks/useSetInitTransaction'
import BackIcon from 'assets/images/rectangle/right-green.svg'
import Footer from './footer'
import { MoneyPoolTransactionTypes } from '../../../utilities/helper/moneyPools'
import Switch from 'react-switch'
import useGetInitPaymentOs from 'pages/wallet/hooks/useGetInitPaymentOs'
import {
    getActiveGateways,
    useActiveGatewayCode,
} from 'utilities/hooks/useIsDefaultGateway'
import GatewayPicker from 'components/gatewayPicker'
import ManualPaymentCard, {
    ManualPaymentReason,
} from 'components/manualPayment'

export default function Transaction() {
    const [isWallet, setIsWallet] = useState<boolean>(false)
    const [payment, setPayment] = useState<number>(0)
    const [jsonData, setJsonData] = useState<any>([])
    const [lackOfWallet, setLackOfWallet] = useState<number>(0)
    const { state } = useLocation()
    const { data, isLoading } = useSetInitTransaction(
        state?.poolShipId,
        state?.formikValue?.type,
        state?.formikValue?.amount
    )
    const navigate = useNavigate()
    const profile = JSON.parse(localStorage.getItem('profile')!)
    const { data: walletData } = useGetInitPaymentOs(
        profile?.id,
        Number(data?.commission),
        Number(payment)
    )
    const [selectedCode, setSelectedCode] = useState<string | null>(null)
    const activeGateways = getActiveGateways(data)
    useActiveGatewayCode(data, setSelectedCode)

    useEffect(() => {
        if (data) {
            setPayment(Number(state?.formikValue?.amount))
        }
    }, [data])

    useEffect(() => {
        if (data && payment) {
            if (isWallet) {
                // Compare against what is actually taken. The wallet is
                // debited `total_amount` = amount + commission
                // (`Transaction.total_amount`), while this used to decide
                // "covered" on the amount alone and then compute the shortfall
                // WITH the commission — so a balance between the two read as
                // full cover and the debit would raise a bare ValueError.
                const due = Number(payment) + Number(data.commission)
                const balance = Number(walletData?.wallet_balance ?? 0)
                setLackOfWallet(balance >= due ? 0 : due - balance)
            } else {
                setLackOfWallet(Number(payment) + Number(data.commission))
            }
        }
    }, [data, payment, isWallet])

    useEffect(() => {
        if (payment && state?.moneyPoolId && data && state) {
            setJsonData([
                {
                    moneyPool: Number(state?.moneyPoolId),
                    amount: Number(payment) + Number(data.commission),
                    ctxId: Number(state?.moneyPoolId),
                    gateway: selectedCode === 'SMN_MPG' ? 5 : 9,
                    receiver: data.receivers[0].id,
                    poolShip: state?.poolShipId,
                    trustState: state.trustState,
                    commission: Number(data.commission),
                    source: 'hwt',
                },
            ])
        }
    }, [payment, data, state, lackOfWallet])

    // A «تک‌پرداختی» fund HAS a gateway — it belongs to its manager. The
    // backend now says so up front instead of quoting a gateway that
    // `gateway-payment` then refuses, which is the contradiction the fund page
    // used to walk a member straight into (pwa#30). Answer the question they
    // actually have: where do I send the money?
    //
    // ⚠️ This must NOT short-circuit the whole screen. «پرداخت تنها ثبت شود»
    // lives in the Footer below, and recording needs no gateway at all — the
    // money is already in the manager's hands. Returning early here took the
    // free plan's headline feature («ثبت دستی حساب‌ها») away from the manager
    // of every record-mode fund. Swap the gateway block, keep the rest.
    const recordBox =
        data?.pay_mode === 'record' ? data?.record_boxes?.[0] : undefined

    return (
        <div className=" h-screen relative flex flex-col">
            {data && state && !isLoading ? (
                <>
                    <div className=" my-3 px-4 grow-0">
                        <img
                            src={BackIcon}
                            alt="back"
                            onClick={() => navigate(-1)}
                        />
                    </div>
                    <div className=" grow">
                        <div className="">
                            <>
                                <div className="">
                                    <img
                                        src={HandIcon}
                                        alt="hand"
                                        className=" mx-auto -mb-24 -z-50"
                                    />
                                    <div className="  bg-white shadow-soft rounded-xl px-3 pt-3 pb-8 mx-4 mt-16 min-h-[60%] overflow-y-auto ">
                                        <div className=" flex justify-between mt-4">
                                            <p className="">مبلغ:</p>
                                            <p>
                                                {Number(
                                                    state?.formikValue?.amount
                                                ).toLocaleString()}{' '}
                                                تومان
                                            </p>
                                        </div>
                                        <div className=" flex justify-between mt-4">
                                            <p className="">هزینه اشتراک:</p>
                                            <p>
                                                {data.commission.toLocaleString()}{' '}
                                                تومان
                                            </p>
                                        </div>
                                        <div className=" flex justify-between mt-4">
                                            <p className=""> بابت:</p>
                                            <p>
                                                {
                                                    MoneyPoolTransactionTypes[
                                                        state?.formikValue?.type
                                                    ]
                                                }
                                            </p>
                                        </div>
                                        <div className=" flex justify-between mt-4">
                                            <p className=""> نام صندوق:</p>
                                            <p>{state?.name} </p>
                                        </div>
                                        {state?.isPayingForOther &&
                                            state?.payerName && (
                                                <div className=" flex justify-between mt-4">
                                                    <p className="">
                                                        {' '}
                                                        پرداخت کننده:
                                                    </p>
                                                    <p>{state?.payerName}</p>
                                                </div>
                                            )}
                                        {/* The wallet can only feed an online
                                            payment. On a record-mode fund
                                            there is none, so the switch is a
                                            control that cannot do anything. */}
                                        <div
                                            className={
                                                recordBox
                                                    ? ' hidden'
                                                    : ' flex justify-between mt-4'
                                            }
                                        >
                                            <div className="text-sm">
                                                <p>استفاده از کیف پول:</p>
                                                <p className=" text-text-gray">
                                                    {walletData &&
                                                        walletData?.wallet_balance &&
                                                        walletData?.wallet_balance.toLocaleString()}{' '}
                                                    تومان
                                                </p>
                                            </div>
                                            <Switch
                                                onChange={() =>
                                                    setIsWallet(!isWallet)
                                                }
                                                checked={isWallet}
                                                onColor="#75B844"
                                                onHandleColor="#EAEEF0"
                                                handleDiameter={25}
                                                uncheckedIcon={false}
                                                checkedIcon={false}
                                                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                activeBoxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                height={30}
                                                width={60}
                                                className="react-switch"
                                                id="material-switch"
                                                offColor="#B6BCC8"
                                            />
                                        </div>
                                        <hr className=" my-4" />
                                        <div className=" flex justify-between mt-4">
                                            <p className=""> مبلغ:</p>
                                            <p>
                                                {isWallet
                                                    ? Number(
                                                          lackOfWallet
                                                      ).toLocaleString()
                                                    : Number(
                                                          payment +
                                                              data.commission
                                                      ).toLocaleString()}{' '}
                                                تومان
                                            </p>
                                        </div>
                                    </div>

                                    <div className="px-4 my-4">
                                        {recordBox ? (
                                            <ManualPaymentCard
                                                info={recordBox.manual_payment}
                                                boxName={
                                                    recordBox.box_name ||
                                                    state?.name
                                                }
                                                amount={
                                                    Number(
                                                        state?.formikValue
                                                            ?.amount
                                                    ) || undefined
                                                }
                                                source="fund_deposit"
                                                reason={
                                                    (recordBox.reason as ManualPaymentReason) ??
                                                    'no_gateway'
                                                }
                                            />
                                        ) : (
                                            <GatewayPicker
                                                gateways={activeGateways}
                                                selectedCode={selectedCode}
                                                onSelect={setSelectedCode}
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        </div>
                    </div>
                    <div className=" grow -0 w-full">
                        <Footer
                            total={Number(state?.formikValue?.amount)}
                            commission={data.commission}
                            ctx_id={state?.poolShipId}
                            ctx_type="mnp"
                            destination={data.receivers[0].destination}
                            gateway={selectedCode === 'SMN_MPG' ? 5 : 9}
                            selectedGatewayCode={selectedCode}
                            reciever={data.receivers[0].id}
                            role={state?.role}
                            lackOfWallet={lackOfWallet}
                            isWallet={isWallet}
                            memberId={profile?.id}
                            payment={payment}
                            jsonData={jsonData}
                            isRecordOnly={!!recordBox}
                        />
                    </div>
                </>
            ) : (
                <DotsLoading />
            )}
        </div>
    )
}
